import React, { useEffect, useRef } from 'react';

export const WebGLShader = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // --- WebGL Shader Code ---
  const vertexShaderSource = `
    attribute vec2 a_position;
    varying vec2 v_texCoord;
    void main() {
      v_texCoord = a_position * 0.5 + 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const fragmentShaderSource = `
    precision highp float;
    uniform float u_time;
    varying vec2 v_texCoord;

    // 2D Noise function (Simplex-like)
    vec2 hash( vec2 p ) {
      p = vec2( dot(p,vec2(127.1,311.7)), dot(p,vec2(269.5,183.3)) );
      return -1.0 + 2.0*fract(sin(p)*43758.5453123);
    }

    float noise( in vec2 p ) {
      const float K1 = 0.366025404;
      const float K2 = 0.211324865;
      vec2 i = floor( p + (p.x+p.y)*K1 );
      vec2 a = p - i + (i.x+i.y)*K2;
      vec2 o = (a.x>a.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
      vec2 b = a - o + K2;
      vec2 c = a - 1.0 + 2.0*K2;
      vec3 h = max( 0.5-vec3(dot(a,a), dot(b,b), dot(c,c) ), 0.0 );
      vec3 n = h*h*h*h*vec3( dot(a,hash(i+0.0)), dot(b,hash(i+o)), dot(c,hash(i+1.0)));
      return dot( n, vec3(70.0) );
    }

    float fbm(vec2 uv) {
      float f = 0.0;
      mat2 m = mat2( 1.6,  1.2, -1.2,  1.6 );
      f  = 0.5000 * noise(uv); uv = m * uv;
      f += 0.2500 * noise(uv); uv = m * uv;
      f += 0.1250 * noise(uv); uv = m * uv;
      f += 0.0625 * noise(uv);
      return f;
    }

    void main() {
      vec2 uv = v_texCoord;
      // Center the flame and adjust aspect ratio
      uv.x = uv.x * 2.0 - 1.0; 

      // Create the basic flame shape, tapering at the top
      float flameShape = 1.0 - abs(uv.x);
      flameShape *= pow(v_texCoord.y, 0.4);
      
      // Animate the noise over time to create a flickering effect
      float time = u_time * 1.2;
      float noiseValue = fbm(vec2(uv.x * 2.0, v_texCoord.y * 2.0 - time));
      
      // Combine shape and noise
      float finalFlame = flameShape * (1.0 - noiseValue * 0.7);

      // Color palette for the flame
      vec3 color = vec3(1.5 * finalFlame, 1.0 * finalFlame, 0.0);
      color = mix(color, vec3(1.0, 0.2, 0.0), smoothstep(0.4, 0.8, finalFlame));
      color = mix(vec3(0.1, 0.2, 1.0), color, smoothstep(0.0, 0.15, finalFlame));

      gl_FragColor = vec4(color, finalFlame * 0.8);
    }
  `;

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl', { antialias: true });
    canvas.width = 400;
    canvas.height = 400;

    if (!gl) {
      console.error("WebGL is not supported in this browser.");
      return;
    }

    const createShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error: ' + gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Shader program linking error: ' + gl.getProgramInfoLog(program));
      return;
    }

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const startTime = Date.now();
    const animate = () => {
      const currentTime = (Date.now() - startTime) / 1000;
      gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.useProgram(program);

      // Set uniforms
      const timeLocation = gl.getUniformLocation(program, "u_time");
      gl.uniform1f(timeLocation, currentTime);
      
      // Set up attributes
      const positionLocation = gl.getAttribLocation(program, "a_position");
      gl.enableVertexAttribArray(positionLocation);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      // Enable blending for a flame effect
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-900">
      <canvas ref={canvasRef} className="border border-gray-700 rounded-lg" />
      <div className="mt-4 text-white text-center">
        <h3 className="text-xl font-bold">WebGL Shader</h3>
        <p className="text-gray-400">Procedural noise on the GPU</p>
      </div>
    </div>
  );
};
